import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiFileText, FiList, FiBarChart2, FiPieChart, FiDownload } = FiIcons;

const ReportingSuite = () => {
  const [activeTab, setActiveTab] = useState('weekly');

  const templates = {
    weekly: {
      title: 'Weekly Pulse Report',
      description: 'High-frequency check-in for immediate tactical adjustments.',
      sections: [
        { title: 'Executive Summary', content: '3-bullet highlight of the week\'s biggest wins or blockers.' },
        { title: 'Traffic & Acquisition', content: 'WoW (Week over Week) traffic comparison by channel.' },
        { title: 'Campaign Status', content: 'Green/Yellow/Red status on active campaigns.' },
        { title: 'Content Output', content: 'List of assets published vs. planned.' },
        { title: 'Next Week Priorities', content: 'Top 3 focus areas for the upcoming sprint.' }
      ]
    },
    monthly: {
      title: 'Monthly Strategy Review',
      description: 'Deep-dive analysis for strategic decision making.',
      sections: [
        { title: 'Performance Overview', content: 'MoM (Month over Month) and YoY (Year over Year) analysis.' },
        { title: 'Channel Deep Dive', content: 'Detailed breakdown of SEO, Email, and Paid performance.' },
        { title: 'Conversion Analysis', content: 'Funnel drop-off points and CRO opportunities.' },
        { title: 'ROI & Attribution', content: 'Cost per acquisition and revenue attribution model results.' },
        { title: 'Strategic Recommendations', content: 'Data-backed suggestions for strategy pivots.' }
      ]
    }
  };

  const kpiDictionary = [
    { term: 'CTR (Click-Through Rate)', def: 'Percentage of people who click a link out of total impressions.', benchmark: '2-5%' },
    { term: 'CPA (Cost Per Acquisition)', def: 'Total cost divided by number of conversions.', benchmark: 'Varies by industry' },
    { term: 'Bounce Rate', def: 'Percentage of single-page sessions.', benchmark: '< 40% (Retail), < 60% (Content)' },
    { term: 'Conversion Rate', def: 'Percentage of visitors who complete a desired action.', benchmark: '2-4%' },
    { term: 'MQL (Marketing Qualified Lead)', def: 'Lead judged more likely to become a customer based on engagement.', benchmark: 'N/A' }
  ];

  const attributionModels = [
    { name: 'Last Click', desc: '100% credit goes to the last touchpoint.', useCase: 'Simple conversion paths.' },
    { name: 'First Click', desc: '100% credit goes to the first touchpoint.', useCase: 'Awareness focused campaigns.' },
    { name: 'Linear', desc: 'Credit is distributed equally across all touchpoints.', useCase: 'Multi-channel consistency.' },
    { name: 'Time Decay', desc: 'Touchpoints closer to conversion get more credit.', useCase: 'Long sales cycles.' }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-charcoal mb-4">Reporting Suite</h1>
        <p className="text-lg text-charcoal-500 max-w-3xl">
          Standardized templates and definitions to ensure data consistency and actionable insights across the organization.
        </p>
      </motion.div>

      {/* Navigation Tabs */}
      <div className="flex space-x-4 mb-8 overflow-x-auto pb-2">
        {[
          { id: 'weekly', label: 'Weekly Template', icon: FiFileText },
          { id: 'monthly', label: 'Monthly Template', icon: FiBarChart2 },
          { id: 'kpi', label: 'KPI Dictionary', icon: FiList },
          { id: 'attribution', label: 'Attribution Models', icon: FiPieChart }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-5 py-3 rounded-xl font-bold transition-all whitespace-nowrap border ${
              activeTab === tab.id
                ? 'bg-sage text-white border-sage shadow-md'
                : 'bg-white text-charcoal border-sand-200 hover:bg-sand-50'
            }`}
          >
            <SafeIcon icon={tab.icon} className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="glass-card rounded-xl p-8 min-h-[400px] border-t-4 border-t-sage">
        {activeTab === 'weekly' || activeTab === 'monthly' ? (
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-2xl font-bold text-charcoal mb-2">{templates[activeTab].title}</h2>
                <p className="text-charcoal-500">{templates[activeTab].description}</p>
              </div>
              <button className="flex items-center space-x-2 px-4 py-2 border border-sage-200 text-sage-800 rounded-lg hover:bg-sage-50 transition-colors text-sm font-bold">
                <SafeIcon icon={FiDownload} className="w-4 h-4" />
                <span>Download Template</span>
              </button>
            </div>
            
            <div className="grid gap-4">
              {templates[activeTab].sections.map((section, index) => (
                <div key={index} className="p-5 bg-white rounded-lg border border-sand-200 shadow-sm">
                  <h3 className="font-bold text-charcoal mb-1 flex items-center">
                    <span className="w-2 h-2 bg-goldenrod rounded-full mr-2"></span>
                    {section.title}
                  </h3>
                  <p className="text-sm text-charcoal-500 pl-4">{section.content}</p>
                </div>
              ))}
            </div>
          </motion.div>
        ) : activeTab === 'kpi' ? (
          <motion.div
            key="kpi"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
             <h2 className="text-2xl font-bold text-charcoal mb-6">KPI Dictionary</h2>
             <div className="overflow-hidden border border-sand-200 rounded-lg bg-white">
               <table className="min-w-full divide-y divide-sand-200">
                 <thead className="bg-sand-50">
                   <tr>
                     <th className="px-6 py-4 text-left text-xs font-bold text-charcoal-500 uppercase tracking-wider">Metric</th>
                     <th className="px-6 py-4 text-left text-xs font-bold text-charcoal-500 uppercase tracking-wider">Definition</th>
                     <th className="px-6 py-4 text-left text-xs font-bold text-charcoal-500 uppercase tracking-wider">Benchmark</th>
                   </tr>
                 </thead>
                 <tbody className="bg-white divide-y divide-sand-100">
                   {kpiDictionary.map((item, idx) => (
                     <tr key={idx}>
                       <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-sage-800">{item.term}</td>
                       <td className="px-6 py-4 text-sm text-charcoal-500">{item.def}</td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm text-charcoal font-medium bg-sand-50">{item.benchmark}</td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>
          </motion.div>
        ) : (
          <motion.div
            key="attribution"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h2 className="text-2xl font-bold text-charcoal mb-6">Attribution Models</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {attributionModels.map((model, idx) => (
                <div key={idx} className="border border-sand-200 rounded-lg p-6 hover:border-sage transition-colors bg-white">
                  <div className="flex items-center mb-3">
                    <div className="w-8 h-8 bg-lavender-100 rounded-full flex items-center justify-center mr-3">
                      <SafeIcon icon={FiPieChart} className="w-4 h-4 text-lavender" />
                    </div>
                    <h3 className="font-bold text-charcoal">{model.name}</h3>
                  </div>
                  <p className="text-sm text-charcoal-500 mb-3">{model.desc}</p>
                  <div className="text-xs font-bold bg-sand-100 inline-block px-2 py-1 rounded text-charcoal-500 uppercase tracking-wider">
                    Best for: {model.useCase}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ReportingSuite;